/**
 * WizardManager — Full-page guided onboarding wizard for glass configuration.
 *
 * Design principles:
 * - Full-page overlay, shown immediately (no waiting for data)
 * - "Weiss ich nicht" on every step (skips that filter)
 * - VKF number entry as an alternative fast-track
 * - Expert mode always accessible
 * - Explicit "Weiter" button (no auto-advance)
 * - Summary toast + highlight on finish
 */
export default class WizardManager {
  constructor() {
    this.bridge = null; // set later via setBridge()
    this.currentStep = 0;
    this.selections = {};
    this.overlay = null;
    this.vkfResult = null;
    this._finishing = false;

    this.steps = [
      {
        id: 'entry',
        type: 'entry',
      },
      {
        id: 'class',
        question: 'Wie lange muss das Glas im Brandfall standhalten?',
        subtitle: 'Die Feuerwiderstandsklasse steht in der Bauvorschrift oder im Architekten-Devis.',
        type: 'cards',
        options: [
          {
            value: 'EI30', label: 'EI 30',
            desc: '30 Minuten Feuerwiderstand',
            detail: 'Für Innenwände, Trennwände, Türverglasungen',
            icon: 'fa-shield-alt',
          },
          {
            value: 'EI60', label: 'EI 60',
            desc: '60 Minuten Feuerwiderstand',
            detail: 'Für Treppenhäuser, Korridore, Brandabschnitte',
            icon: 'fa-shield-alt',
          },
          {
            value: 'EI90', label: 'EI 90',
            desc: '90 Minuten Feuerwiderstand',
            detail: 'Für Fluchtwege, Hochhäuser, besondere Anforderungen',
            icon: 'fa-shield-alt',
          },
        ]
      },
      {
        id: 'application',
        question: 'Wo wird das Glas eingebaut?',
        subtitle: 'Bestimmt den UV-Schutz und die Glasoberfläche.',
        type: 'cards',
        options: [
          {
            value: 'Inside', label: 'Innenwand',
            desc: 'Trennwand, Büroverglasung, Innenbereich',
            detail: 'Kein UV-Schutz nötig',
            icon: 'fa-building',
          },
          {
            value: 'Inside/Outside', label: 'Fassade',
            desc: 'Aussenwand mit einer Seite innen',
            detail: 'UV-Schutz auf der Aussenseite',
            icon: 'fa-home',
          },
          {
            value: 'Outside/Outside', label: 'Fassade beidseitig',
            desc: 'Beide Seiten sind der Witterung ausgesetzt',
            detail: 'Beidseitiger UV-Schutz',
            icon: 'fa-sun',
          },
        ]
      },
      {
        id: 'structure',
        question: 'Benötigen Sie Wärmedämmung?',
        subtitle: 'Isolierglas verbessert die Energieeffizienz — wichtig bei Fassaden.',
        type: 'cards',
        options: [
          {
            value: '1-glazed (mono)', label: 'Nein, Einfachglas',
            desc: 'Leichter, günstiger — keine Isolierung',
            detail: 'Für Innenwände ohne Dämmungsanforderung',
            icon: 'fa-layer-group',
          },
          {
            value: '2-glazed-ISO', label: 'Ja, Standard-Isolierung',
            desc: '2-fach Isolierglas — gute Wärmedämmung',
            detail: 'Für die meisten Fassadenanwendungen',
            icon: 'fa-layer-group',
          },
          {
            value: '3-glazed-ISO', label: 'Ja, maximale Isolierung',
            desc: '3-fach Isolierglas — beste Energieeffizienz',
            detail: 'Für Passivhäuser und hohe Anforderungen',
            icon: 'fa-layer-group',
          },
        ]
      },
      {
        id: 'requirements',
        question: 'Haben Sie bestimmte Anforderungen?',
        subtitle: 'Diese Angaben sind optional — leer lassen wenn unbekannt.',
        type: 'inputs',
        fields: [
          {
            id: 'maxThickness',
            label: 'Maximale Glasdicke',
            placeholder: 'z.B. 50',
            suffix: 'mm',
            icon: 'fa-ruler-vertical',
            hint: 'Rahmensystem oder Profil gibt oft eine max. Dicke vor.',
            inputMode: 'decimal',
          },
          {
            id: 'minUValue',
            label: 'Mindest-Wärmedämmung (U-Wert)',
            placeholder: 'z.B. 0.7',
            suffix: 'W/m²K',
            icon: 'fa-thermometer-half',
            hint: 'Je tiefer der U-Wert, desto besser die Dämmung.',
            inputMode: 'decimal',
          },
          {
            id: 'targetSound',
            label: 'Ziel-Schalldämmwert',
            placeholder: 'z.B. 42',
            suffix: 'dB',
            icon: 'fa-volume-down',
            hint: 'Rw-Wert in Dezibel — höher ist besser.',
            inputMode: 'decimal',
            skipOption: 'Schalldämmung hat keine Priorität',
          },
        ],
      },
      {
        id: 'resistance',
        question: 'Ist Einbruchschutz erforderlich?',
        subtitle: 'Durchschlaghemmung nach EN 1627 — oft Vorschrift bei Erdgeschoss-Fenstern.',
        type: 'cards',
        options: [
          {
            value: '-', label: 'Nein',
            desc: 'Kein besonderer Einbruchschutz nötig',
            icon: 'fa-lock-open',
          },
          {
            value: 'RC 2 (WK 2) / P4A', label: 'Basisschutz (RC2)',
            desc: 'Schutz gegen Gelegenheitseinbrecher',
            detail: 'Widerstandsklasse 2, oft als Mindeststandard gefordert',
            icon: 'fa-lock',
          },
          {
            value: 'RC 3 (WK 3) / P5A', label: 'Erhöhter Schutz (RC3)',
            desc: 'Schutz gegen erfahrene Einbrecher',
            detail: 'Widerstandsklasse 3 mit schwerem Werkzeug',
            icon: 'fa-lock',
          },
        ]
      },
    ];
  }

  // ── Public API ──────────────────────────────────────────────

  /** Connect to the UIBridge (called later when bridge is ready). */
  setBridge(bridge) {
    this.bridge = bridge;
  }

  show() {
    if (!this.overlay) this._buildOverlay();
    this.currentStep = 0;
    this.selections = {};
    this.vkfResult = null;
    this._finishing = false;
    this.overlay.classList.remove('hidden');
    document.body.classList.add('wizard-open');
    this._renderStep();
  }

  hide() {
    if (this.overlay) this.overlay.classList.add('hidden');
    document.body.classList.remove('wizard-open');
  }

  isVisible() {
    return this.overlay && !this.overlay.classList.contains('hidden');
  }

  // ── Build overlay ───────────────────────────────────────────

  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'wiz-overlay';
    this.overlay.id = 'wizard-overlay';
    this.overlay.innerHTML = '<div class="wiz-content" id="wiz-content"></div>';
    document.body.appendChild(this.overlay);
  }

  // ── Render current step ─────────────────────────────────────

  _renderStep() {
    const content = this.overlay.querySelector('#wiz-content');
    const step = this.steps[this.currentStep];

    if (step.type === 'entry') {
      this._renderEntryStep(content);
    } else if (step.type === 'cards') {
      this._renderCardStep(content, step);
    } else if (step.type === 'inputs') {
      this._renderInputStep(content, step);
    }

    // Animate in
    requestAnimationFrame(() => {
      content.classList.remove('wiz-slide-in');
      void content.offsetWidth;
      content.classList.add('wiz-slide-in');
    });
  }

  // ── Entry step (landing page) ──────────────────────────────

  _renderEntryStep(container) {
    container.innerHTML = `
      <div class="wiz-page wiz-entry">
        <div class="wiz-header">
          <img class="wiz-logo" src="cdr-logo.0fc37c88.svg" alt="CDR">
          <button class="wiz-skip" id="wiz-skip">
            <i class="fas fa-sliders-h"></i> <span class="wiz-skip-label">Experten-Modus</span>
          </button>
        </div>
        <div class="wiz-hero" id="wiz-hero">
          <h1 class="wiz-title">Finden Sie das richtige Brandschutzglas</h1>
          <p class="wiz-lead">Wir führen Sie in wenigen Schritten zur passenden Glaskonfiguration — kein Vorwissen nötig.</p>
        </div>
        <div class="wiz-entry-paths" id="wiz-entry-paths">
          <button class="wiz-path-card wiz-path-main" id="wiz-start-guided">
            <div class="wiz-path-icon"><i class="fas fa-compass"></i></div>
            <div class="wiz-path-text">
              <span class="wiz-path-title">Schritt für Schritt konfigurieren</span>
              <span class="wiz-path-desc">Wir stellen Ihnen einfache Fragen und finden das passende Glas.</span>
            </div>
            <i class="fas fa-arrow-right wiz-path-arrow"></i>
          </button>
          <button class="wiz-path-card" id="wiz-start-vkf">
            <div class="wiz-path-icon"><i class="fas fa-search"></i></div>
            <div class="wiz-path-text">
              <span class="wiz-path-title">VKF-Nummer eingeben</span>
              <span class="wiz-path-desc">Sie haben eine VKF-Nummer vom Devis oder Architekten? Glas direkt finden.</span>
            </div>
            <i class="fas fa-arrow-right wiz-path-arrow"></i>
          </button>
        </div>
        <div class="wiz-vkf-panel hidden" id="wiz-vkf-panel">
          <h2 class="wiz-question">VKF-Nummer eingeben</h2>
          <p class="wiz-subtitle">Die VKF-Nummer finden Sie im Devis, in der Bauvorschrift oder beim Systemanbieter.</p>
          <div class="wiz-vkf-input-row">
            <input type="text" class="wiz-vkf-input" id="wiz-vkf-input"
              placeholder="z.B. 8893" autocomplete="off" inputmode="numeric">
            <button class="wiz-btn wiz-btn-primary" id="wiz-vkf-search">
              <i class="fas fa-search"></i> Suchen
            </button>
          </div>
          <div class="wiz-vkf-result hidden" id="wiz-vkf-result"></div>
          <button class="wiz-link wiz-vkf-back" id="wiz-vkf-back">
            <i class="fas fa-arrow-left"></i> Zurück zur Übersicht
          </button>
        </div>
      </div>
    `;

    container.querySelector('#wiz-skip').addEventListener('click', () => this._exitToExpert());
    container.querySelector('#wiz-start-guided').addEventListener('click', () => {
      this.currentStep = 1;
      this._renderStep();
    });

    const vkfPanel = container.querySelector('#wiz-vkf-panel');
    const pathCards = container.querySelector('#wiz-entry-paths');
    const heroEl = container.querySelector('#wiz-hero');

    container.querySelector('#wiz-start-vkf').addEventListener('click', () => {
      pathCards.classList.add('hidden');
      heroEl.classList.add('hidden');
      vkfPanel.classList.remove('hidden');
      setTimeout(() => container.querySelector('#wiz-vkf-input').focus(), 100);
    });

    container.querySelector('#wiz-vkf-back').addEventListener('click', () => {
      vkfPanel.classList.add('hidden');
      pathCards.classList.remove('hidden');
      heroEl.classList.remove('hidden');
    });

    container.querySelector('#wiz-vkf-search').addEventListener('click', () => this._searchVkf());
    container.querySelector('#wiz-vkf-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this._searchVkf();
    });
  }

  // ── VKF search ──────────────────────────────────────────────

  _searchVkf() {
    const input = this.overlay.querySelector('#wiz-vkf-input');
    const resultEl = this.overlay.querySelector('#wiz-vkf-result');
    const vkf = (input.value || '').trim();

    if (!vkf) {
      input.classList.add('wiz-shake');
      setTimeout(() => input.classList.remove('wiz-shake'), 400);
      return;
    }

    const map = window._cdrVkfMap || {};
    const glass = map[vkf];

    resultEl.classList.remove('hidden');

    if (!window._cdrVkfMap) {
      resultEl.innerHTML = `
        <div class="wiz-vkf-notfound">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Glasdaten werden noch geladen — bitte kurz warten.</span>
        </div>
      `;
      return;
    }

    if (glass) {
      this.vkfResult = glass;
      resultEl.innerHTML = `
        <div class="wiz-vkf-found">
          <div class="wiz-vkf-found-icon"><i class="fas fa-check-circle"></i></div>
          <div class="wiz-vkf-found-info">
            <span class="wiz-vkf-found-name">${glass.name}</span>
            <span class="wiz-vkf-found-details">
              ${glass.class} · ${glass.manufacturer} · ${glass.thickness} mm
            </span>
          </div>
        </div>
        <button class="wiz-btn wiz-btn-primary wiz-btn-full" id="wiz-vkf-apply">
          <i class="fas fa-check"></i> Dieses Glas übernehmen
        </button>
      `;
      resultEl.querySelector('#wiz-vkf-apply').addEventListener('click', () => {
        this._applyVkfResult(glass);
      });
    } else {
      resultEl.innerHTML = `
        <div class="wiz-vkf-notfound">
          <i class="fas fa-info-circle"></i>
          <span>Keine Übereinstimmung für VKF-Nr. <strong>${vkf}</strong> gefunden.</span>
        </div>
        <button class="wiz-link" id="wiz-vkf-to-guided">
          <i class="fas fa-compass"></i> Stattdessen Schritt für Schritt konfigurieren
        </button>
      `;
      resultEl.querySelector('#wiz-vkf-to-guided').addEventListener('click', () => {
        this.currentStep = 1;
        this._renderStep();
      });
    }
  }

  _applyVkfResult(glass) {
    if (!this.bridge) { this._finish(); return; }
    if (glass.class) this.bridge.setFilterValue('class', glass.class);
    if (glass.application) this.bridge.setFilterValue('application', glass.application);
    if (glass.structure) {
      const structMap = { 'mono': '1-glazed (mono)' };
      this.bridge.setFilterValue('structure', structMap[glass.structure] || glass.structure);
    }
    if (glass.resistance && glass.resistance !== '-') {
      this.bridge.setFilterValue('resistance', glass.resistance);
    }
    if (glass.whiteGlass === 'Yes') {
      this.bridge.setFilterValue('white-glass', true);
    }
    // Store selections for summary
    this.selections = {
      class: glass.class,
      application: glass.application,
      structure: glass.structure,
      resistance: glass.resistance,
    };
    this._finish();
  }

  // ── Card step — auto-advance on selection ──

  _renderCardStep(container, step) {
    const totalGuided = this.steps.length - 1;
    const guidedIdx = this.currentStep;
    const isFirst = this.currentStep <= 1;

    container.innerHTML = `
      <div class="wiz-page wiz-step">
        <div class="wiz-header">
          <button class="wiz-back" id="wiz-back" ${isFirst ? 'style="visibility:hidden"' : ''}>
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="wiz-progress-bar">
            <div class="wiz-progress-fill" style="width: ${(guidedIdx / totalGuided) * 100}%"></div>
          </div>
          <button class="wiz-skip" id="wiz-skip">
            <i class="fas fa-sliders-h"></i> <span class="wiz-skip-label">Experten-Modus</span>
          </button>
        </div>
        <div class="wiz-step-body">
          <div class="wiz-step-counter">Schritt ${guidedIdx} von ${totalGuided}</div>
          <h2 class="wiz-question">${step.question}</h2>
          <p class="wiz-subtitle">${step.subtitle}</p>
          <div class="wiz-cards" id="wiz-cards"></div>
        </div>
      </div>
    `;

    const cardsEl = container.querySelector('#wiz-cards');

    step.options.forEach((opt, i) => {
      const card = document.createElement('button');
      card.className = 'wiz-card';
      card.style.animationDelay = `${i * 0.06}s`;
      card.innerHTML = `
        <div class="wiz-card-left">
          ${opt.icon ? `<div class="wiz-card-icon"><i class="fas ${opt.icon}"></i></div>` : ''}
          <div class="wiz-card-text">
            <span class="wiz-card-label">${opt.label}</span>
            <span class="wiz-card-desc">${opt.desc}</span>
            ${opt.detail ? `<span class="wiz-card-detail">${opt.detail}</span>` : ''}
          </div>
        </div>
        <div class="wiz-card-check"><i class="fas fa-check"></i></div>
      `;
      card.addEventListener('click', () => {
        this.selections[step.id] = opt.value;
        cardsEl.querySelectorAll('.wiz-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        // Auto-advance after brief visual feedback
        setTimeout(() => this._goNext(), 350);
      });
      cardsEl.appendChild(card);
    });

    // "Weiss ich nicht" as a full-width card at the bottom
    const idkCard = document.createElement('button');
    idkCard.className = 'wiz-card wiz-card-idk';
    idkCard.style.animationDelay = `${step.options.length * 0.06}s`;
    idkCard.innerHTML = `
      <div class="wiz-card-left">
        <div class="wiz-card-icon"><i class="fas fa-question"></i></div>
        <div class="wiz-card-text">
          <span class="wiz-card-label">Weiss ich nicht</span>
          <span class="wiz-card-desc">Überspringen — kann später angepasst werden</span>
        </div>
      </div>
      <div class="wiz-card-check"><i class="fas fa-arrow-right"></i></div>
    `;
    idkCard.addEventListener('click', () => {
      delete this.selections[step.id];
      idkCard.classList.add('selected');
      setTimeout(() => this._goNext(), 300);
    });
    cardsEl.appendChild(idkCard);

    // Navigation
    container.querySelector('#wiz-back').addEventListener('click', () => this._goBack());
    container.querySelector('#wiz-skip').addEventListener('click', () => this._exitToExpert());
  }

  // ── Input step (numeric inputs with optional skip) ─────────

  _renderInputStep(container, step) {
    const totalGuided = this.steps.length - 1;
    const guidedIdx = this.currentStep;

    container.innerHTML = `
      <div class="wiz-page wiz-step">
        <div class="wiz-header">
          <button class="wiz-back" id="wiz-back">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="wiz-progress-bar">
            <div class="wiz-progress-fill" style="width: ${(guidedIdx / totalGuided) * 100}%"></div>
          </div>
          <button class="wiz-skip" id="wiz-skip">
            <i class="fas fa-sliders-h"></i> <span class="wiz-skip-label">Experten-Modus</span>
          </button>
        </div>
        <div class="wiz-step-body">
          <div class="wiz-step-counter">Schritt ${guidedIdx} von ${totalGuided}</div>
          <h2 class="wiz-question">${step.question}</h2>
          <p class="wiz-subtitle">${step.subtitle}</p>
          <div class="wiz-inputs" id="wiz-inputs"></div>
          <div class="wiz-input-actions">
            <button class="wiz-btn wiz-btn-primary wiz-btn-full" id="wiz-input-next">
              Weiter <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    `;

    const inputsEl = container.querySelector('#wiz-inputs');

    step.fields.forEach((field, i) => {
      const group = document.createElement('div');
      group.className = 'wiz-input-group';
      group.style.animationDelay = `${i * 0.08}s`;

      const hasSkip = !!field.skipOption;

      group.innerHTML = `
        <label class="wiz-input-label" for="wiz-field-${field.id}">
          ${field.icon ? `<i class="fas ${field.icon} wiz-input-icon"></i>` : ''}
          ${field.label}
        </label>
        <div class="wiz-input-row">
          <input type="text" class="wiz-input-field" id="wiz-field-${field.id}"
            placeholder="${field.placeholder}" inputmode="${field.inputMode || 'text'}"
            autocomplete="off" ${hasSkip && this.selections[field.id] === 'skip' ? 'disabled' : ''}>
          <span class="wiz-input-suffix">${field.suffix}</span>
        </div>
        ${field.hint ? `<p class="wiz-input-hint">${field.hint}</p>` : ''}
        ${hasSkip ? `
          <label class="wiz-input-skip-row">
            <input type="checkbox" class="wiz-input-skip-cb" data-field="${field.id}"
              ${this.selections[field.id] === 'skip' ? 'checked' : ''}>
            <span class="wiz-input-skip-label">${field.skipOption}</span>
          </label>
        ` : ''}
      `;

      inputsEl.appendChild(group);

      // Restore previous value if any
      const inputEl = group.querySelector(`#wiz-field-${field.id}`);
      if (this.selections[field.id] && this.selections[field.id] !== 'skip') {
        inputEl.value = this.selections[field.id];
      }

      // Handle skip checkbox toggle
      if (hasSkip) {
        const cb = group.querySelector('.wiz-input-skip-cb');
        cb.addEventListener('change', () => {
          inputEl.disabled = cb.checked;
          if (cb.checked) {
            inputEl.value = '';
            inputEl.classList.add('wiz-input-disabled');
          } else {
            inputEl.classList.remove('wiz-input-disabled');
            inputEl.focus();
          }
        });
        // Apply initial state
        if (cb.checked) inputEl.classList.add('wiz-input-disabled');
      }
    });

    // Collect values and advance
    container.querySelector('#wiz-input-next').addEventListener('click', () => {
      step.fields.forEach(field => {
        const inputEl = container.querySelector(`#wiz-field-${field.id}`);
        const skipCb = container.querySelector(`.wiz-input-skip-cb[data-field="${field.id}"]`);

        if (skipCb && skipCb.checked) {
          this.selections[field.id] = 'skip';
        } else if (inputEl.value.trim()) {
          this.selections[field.id] = inputEl.value.trim();
        } else {
          delete this.selections[field.id];
        }
      });
      this._goNext();
    });

    // Navigation
    container.querySelector('#wiz-back').addEventListener('click', () => this._goBack());
    container.querySelector('#wiz-skip').addEventListener('click', () => this._exitToExpert());
  }

  // ── Navigation ──────────────────────────────────────────────

  _goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this._renderStep();
    }
  }

  _goNext() {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      this._renderStep();
    } else {
      this._finish();
    }
  }

  _finish() {
    if (this._finishing) return;
    this._finishing = true;

    // Store numeric requirements as globals for filtering/display
    const maxTh = parseFloat(this.selections.maxThickness);
    if (!isNaN(maxTh) && maxTh > 0) {
      window._cdrMaxThickness = maxTh;
    }

    const minU = parseFloat(this.selections.minUValue);
    if (!isNaN(minU) && minU > 0) {
      window._cdrMinUValue = minU;
    }

    if (this.selections.targetSound && this.selections.targetSound !== 'skip') {
      const targetDb = parseFloat(this.selections.targetSound);
      if (!isNaN(targetDb) && targetDb > 0) {
        window._cdrTargetSound = targetDb;
      }
    }

    localStorage.setItem('cdr-wizard-v2', 'true');

    // Don't apply filters yet — the reveal sequence applies them one by one
    this._runRevealSequence();
  }

  _exitToExpert() {
    this.hide();
    localStorage.setItem('cdr-wizard-v2', 'true');
  }

  // ── Sequential reveal: scroll → highlight → apply, one card at a time ──

  _runRevealSequence() {
    const cardFilterIds = ['class', 'application', 'structure', 'resistance'];
    const steps = cardFilterIds
      .filter(k => this.selections[k])
      .map(k => ({ filterId: k, value: this.selections[k] }));

    // Fade out wizard overlay
    this.overlay.classList.add('wiz-fade-out');

    setTimeout(() => {
      this.hide();
      this.overlay.classList.remove('wiz-fade-out');

      // Step through cards sequentially
      let delay = 300;
      steps.forEach((step, i) => {
        setTimeout(() => {
          const card = document.querySelector(`.property-card[data-filter="${step.filterId}"]`);
          if (!card) return;

          // Scroll into view
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          // Highlight the card
          card.classList.add('wiz-highlight');

          // Apply the filter value (updates the active segment button)
          if (this.bridge) {
            this.bridge.setFilterValue(step.filterId, step.value);
          }

          // Remove highlight after it plays
          setTimeout(() => card.classList.remove('wiz-highlight'), 900);
        }, i * 700);
      });
    }, 500);
  }
}
